import moment from "moment-timezone";
import { uniqBy } from "lodash";
import { useTranslation } from "react-i18next";
import Checkbox from "components/shared/inputs/Checkbox";
import Tooltip from "components/shared/Tooltip";
import BreakOvertimeIndicator from "../../../../../Component/components/BreakOvertimeIndicator";
import ShiftDetails from "./ShiftDetails";
import { getSectionDurations } from "../../../../../Component/helpers/data/getSectionDurations";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { useRequestCutoff } from "../../../../../Component/hooks/useRequestCutoff";
import { useQualificationPreferences } from "../../../../../Component/hooks/useQualificationPreferences";
import { renderSettingsResourceLabel } from "utils/helpers/settings";
import { cn } from "lib/utils";
import type { Shift, ShiftPatch } from "./types";

const QUALIFICATION_FIELDS = ["skills", "licenses", "certifications"] as const;

interface ShiftRowProps {
    event: any;
    shift?: Shift;
    errors?: any;
    onToggle: (checked: boolean) => void;
    onChange: (patch: ShiftPatch) => void;
}

function ShiftRow({ event, shift, errors, onToggle, onChange }: ShiftRowProps) {
    const dateFormat = useCompanyDateFormat();
    const timeFormat = useCompanyTimeFormat();
    const { t } = useTranslation();
    const { hours, isTooLate } = useRequestCutoff();
    const enabledQualifications = useQualificationPreferences();
    const disabled = !shift && isTooLate(event.startDate);

    const section = event.sections?.[0];
    const { breakDuration, overtimeDuration } = getSectionDurations(event.sections);
    const resourceLabel = [section?.job, section?.location]
        .filter(Boolean)
        .map(renderSettingsResourceLabel)
        .join(" · ");
    const qualifications = QUALIFICATION_FIELDS
        .filter((field) => enabledQualifications[field])
        .map((field) => ({
            field,
            items: uniqBy(
                (event.sections ?? []).flatMap((item: any) => item?.[field] ?? []).filter(Boolean),
                (item: any) => item?.id ?? item
            ),
        }))
        .filter(({ items }) => items.length > 0);

    const row = (
        <label
            className={cn(
                "tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-text-sm",
                disabled ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer hover:tw-bg-gray-50"
            )}
        >
            <Checkbox
                checked={!!shift}
                disabled={disabled}
                onCheckedChange={(checked) => onToggle(checked === true)}
            />
            <span className="tw-w-28 tw-font-medium">
                {moment(event.startDate).format(dateFormat)}
            </span>
            <span className="tw-w-36 tw-text-gray-600">
                {moment(event.startDate).format(timeFormat)} - {moment(event.endDate).format(timeFormat)}
            </span>
            <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-text-gray-600">
                <span className="tw-truncate">{resourceLabel}</span>
                {qualifications.map(({ field, items }) => (
                    <span key={field} className="tw-truncate tw-text-xs tw-text-muted-foreground">
                        {`${t(field)}: ${items.map(renderSettingsResourceLabel).join(", ")}`}
                    </span>
                ))}
            </div>
            <BreakOvertimeIndicator
                breakDuration={breakDuration}
                overtimeDuration={overtimeDuration}
                size={14}
            />
        </label>
    );

    return (
        <div className={cn(shift && "tw-bg-primary/5")}>
            {disabled ? (
                <Tooltip trigger={row}>
                    {t("requests-close-amount-hours-before-shift", { time: `${hours}h` })}
                </Tooltip>
            ) : row}

            {shift && <ShiftDetails shift={shift} errors={errors} onChange={onChange} />}
        </div>
    );
}

export default ShiftRow;
